import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/UserOrderSummary.css';
import Toast from './Toast';
import ButtonRegular from './ui/ButtonRegular';

function UserOrderSummary() {
	const [cart, setCart] = useState(null);
	const [loading, setLoading] = useState(false);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [acceptedPolicy, setAcceptedPolicy] = useState(false);
	const [toast, setToast] = useState('');

	const navigate = useNavigate();
	const location = useLocation();
	const form = location.state?.form;

	const VAT_RATE = 0.23;

	useEffect(() => {
		const fetchCart = async () => {
			try {
				const response = await axios.get(
					`${process.env.REACT_APP_API_URL}/api/cart`,
					{ headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
				);
				setCart(response.data);
			} catch (err) {
				console.error('Błąd ładowania koszyka:', err);
				setToast('❌ Nie udało się pobrać koszyka.');
			}
		};
		fetchCart();
	}, []);

	if (!form) {
		return (
			<div className="form-container">
				<div className="form-content">
					<p>Brak danych dostawy.</p>
					<ButtonRegular onClick={() => navigate('/delivery')}>
						Wróć do formularza
					</ButtonRegular>
				</div>
			</div>
		);
	}

	if (!cart) return <p>Ładowanie koszyka...</p>;

	// ===== Produkty + VAT =====
	const cartWithCalculations = cart.items.map((item, index) => {
		const brutto = item.product.price;
		const netto = brutto / (1 + VAT_RATE);
		const totalBrutto = brutto * item.quantity;
		const totalNetto = netto * item.quantity;

		return {
			lp: index + 1,
			productId: item.product._id || item.product.id,
			name: item.product.name,
			quantity: item.quantity,
			priceBrutto: brutto.toFixed(2),
			priceNetto: netto.toFixed(2),
			valueBrutto: totalBrutto.toFixed(2),
			valueNetto: totalNetto.toFixed(2),
		};
	});

	const totalNetto = cartWithCalculations
		.reduce((sum, item) => sum + parseFloat(item.valueNetto), 0)
		.toFixed(2);

	const totalBrutto = cartWithCalculations
		.reduce((sum, item) => sum + parseFloat(item.valueBrutto), 0)
		.toFixed(2);

	const totalWithDelivery = (parseFloat(totalBrutto) + form.delivery.price).toFixed(2);

	// ===== Adresy =====
	const clientAddress = {
		firstName: form.firstName,
		lastName: form.lastName,
		phone: form.phoneNr,
		...form.clientAddress,
		country: 'Polska',
	};

	const shippingAddress = form.differentShipping
		? { ...form.shippingAddress, country: 'Polska' }
		: clientAddress;

	const companyAddress = form.wantsInvoice
		? {
				companyName: form.companyName,
				taxId: form.taxId,
				regon: form.regon,
				...form.companyAddress,
				country: 'Polska',
			}
		: null;

	// ===== Składanie zamówienia =====
	const handlePlaceOrder = async () => {
		if (!acceptedTerms || !acceptedPolicy) {
			setToast('⚠️ Musisz zaakceptować regulamin i politykę prywatności.');
			return;
		}

		setLoading(true);
		try {
			// Tworzymy payload z pełnymi nazwami produktów
			const itemsForOrder = cartWithCalculations.map(i => ({
				product: i.productId,
				name: i.name,
				quantity: i.quantity,
				price: parseFloat(i.priceBrutto),
			}));

			const payload = {
				items: itemsForOrder,
				total: parseFloat(totalWithDelivery),
				delivery: {
					method: form.delivery.method,
					label: form.delivery.label,
					price: form.delivery.price,
				},
				clientAddress,
				shippingAddress,
				payment: {
					method: 'Przelewy24',
					status: 'pending',
				},
				invoice: companyAddress,
			};

			const orderRes = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/orders`,
				payload,
				{ headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
			);

			await axios.delete(`${process.env.REACT_APP_API_URL}/api/cart/clear`, {
				headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
			});

			// Przekazujemy do Receipt pełne items z nazwami
			navigate('/receipt', {
				state: { order: { ...orderRes.data, items: itemsForOrder }, delivery: payload.delivery },
			});
		} catch (err) {
			console.error('Błąd składania zamówienia:', err);
			setToast('❌ Wystąpił błąd podczas składania zamówienia.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="form-container">
			<div className="form-content">
				<h2 className="form-title">Podsumowanie zamówienia</h2>

				{/* ===== PRODUKTY ===== */}
				<table className="summary-table">
					<thead>
						<tr>
							<th>LP</th>
							<th>Nazwa</th>
							<th>Ilość</th>
							<th>Cena netto</th>
							<th>Cena brutto</th>
							<th>Wartość netto</th>
							<th>Wartość brutto</th>
						</tr>
					</thead>
					<tbody>
						{cartWithCalculations.map(item => (
							<tr key={item.lp}>
								<td>{item.lp}</td>
								<td>{item.name}</td>
								<td>{item.quantity}</td>
								<td>{item.priceNetto} PLN</td>
								<td>{item.priceBrutto} PLN</td>
								<td>{item.valueNetto} PLN</td>
								<td>{item.valueBrutto} PLN</td>
							</tr>
						))}
					</tbody>
				</table>

				{/* ===== DOSTAWA ===== */}
				<div className="summary-section" style={{ marginTop: '20px' }}>
	<h3>Dostawa</h3>
	<p>
		{form.delivery.label} – {form.delivery.price.toFixed(2)} PLN
	</p>
				</div>

				{/* ===== SUMY ===== */}
				<div className="summary-total" style={{ marginTop: '12px' }}>
					<p>Suma netto: {totalNetto} PLN</p>
					<p>Suma brutto: {totalBrutto} PLN</p>
					<p>Dostawa: {form.delivery.price.toFixed(2)} PLN</p>
					<p className="summary-pay">Do zapłaty: {totalWithDelivery} PLN</p>
				</div>

				{/* ===== ZGODY ===== */}
				<div className="summary-terms">
					<input
						type="checkbox"
						checked={acceptedTerms}
						onChange={e => setAcceptedTerms(e.target.checked)}
					/>
					<label>Akceptuję <a
				href="/pdf/regulamin.pdf"
				target="_blank"
				rel="noopener noreferrer"
			 className="footer-link">regulamin sklepu</a></label>
				</div>

				<div className="summary-terms">
					<input
						type="checkbox"
						checked={acceptedPolicy}
						onChange={e => setAcceptedPolicy(e.target.checked)}
					/>
					<label>Akceptuję <a
				href="/pdf/politykaprywatnosci.pdf"
				target="_blank"
				rel="noopener noreferrer"
			 className="footer-link">politykę prywatności</a></label>
				</div>

				<div className="summary-actions">
					<ButtonRegular onClick={() => navigate('/delivery')}>
						Edytuj dane dostawy
					</ButtonRegular>
					<ButtonRegular
						onClick={handlePlaceOrder}
						disabled={loading || !acceptedTerms || !acceptedPolicy}
					>
						{loading ? 'Wysyłanie...' : 'Złóż zamówienie'}
					</ButtonRegular>
				</div>
			</div>

			{toast && <Toast message={toast} onClose={() => setToast('')} />}
		</div>
	);
}

export default UserOrderSummary;
